import React, { useState } from 'react';
import type { Settings, SvgIcon } from '../../types';
import SvgEditModal from '../../components/SvgEditModal';
import { useToast } from '../../hooks/useToast';

interface AdminDeveloperPageProps {
    settings: Settings;
    onSave: (settings: Settings) => void;
}

const CodeIcon: React.FC<{className?:string}> = ({className}) => <svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="16 18 22 12 16 6"></polyline><polyline points="8 6 2 12 8 18"></polyline></svg>;
const AlertTriangleIcon: React.FC<{className?:string}> = ({className}) => <svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path><line x1="12" y1="9" x2="12" y2="13"></line><line x1="12" y1="17" x2="12.01" y2="17"></line></svg>;
const DynamicIcon: React.FC<{ icon: SvgIcon, className?: string, color?: string }> = ({ icon, className, color }) => (
    <svg 
        className={className} 
        xmlns="http://www.w3.org/2000/svg" 
        width="24" height="24" 
        viewBox={icon?.viewBox || "0 0 24 24"}
        fill="none" 
        stroke={color || "currentColor"} 
        strokeWidth="2" 
        strokeLinecap="round" 
        strokeLinejoin="round"
        dangerouslySetInnerHTML={{ __html: icon?.svg || '' }}
    />
);

const ToggleSwitch: React.FC<{ enabled: boolean; onChange: () => void; }> = ({ enabled, onChange }) => (
    <button
        type="button"
        className={`${enabled ? 'bg-gray-800' : 'bg-gray-300'} relative inline-flex items-center h-6 rounded-full w-11 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500`}
        onClick={onChange}
        aria-checked={enabled}
        role="switch"
    >
        <span className={`${enabled ? 'translate-x-6' : 'translate-x-1'} inline-block w-4 h-4 transform bg-white rounded-full transition-transform`} />
    </button>
);


const AdminDeveloperPage: React.FC<AdminDeveloperPageProps> = ({ settings, onSave }) => {
    const [formState, setFormState] = useState(settings);
    const [jsonError, setJsonError] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const { showToast } = useToast();
    
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingIcon, setEditingIcon] = useState<{key: string, label: string, icon: SvgIcon} | null>(null);

    const handleSiteConfigChange = (value: string) => {
        setFormState(prev => ({ ...prev, siteConfig: value }));
        try {
            JSON.parse(value);
            setJsonError('');
        } catch (error) {
            setJsonError('Invalid JSON format.');
        }
    };
    
    const handleThemeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormState(prev => ({
            ...prev,
            theme: {
                ...prev.theme,
                backendTheme: {
                    ...prev.theme.backendTheme,
                    [name]: value,
                }
            }
        }));
    };
    
    const handleOpenIconModal = (key: string) => {
        setEditingIcon({
            key,
            label: getIconLabel(key),
            icon: formState.developer[key] || { svg: '', viewBox: '0 0 24 24' },
        });
        setIsModalOpen(true);
    };

    const handleSaveIcon = (newIcon: SvgIcon) => {
        if (editingIcon) {
            setFormState(prev => ({
                ...prev,
                developer: {
                    ...prev.developer,
                    [editingIcon.key]: newIcon,
                }
            }));
        }
        setIsModalOpen(false);
        setEditingIcon(null);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (jsonError) {
            showToast('Cannot save. Please fix the invalid JSON in Site Configuration.', { type: 'error' });
            return;
        }
        setIsSaving(true);
        try {
            await onSave(formState);
            showToast('Developer settings saved successfully!', { type: 'success' });
            window.scrollTo(0, 0);
        } catch(e) {
            showToast('Failed to save settings.', { type: 'error' });
        } finally {
            setIsSaving(false);
        }
    };

    const iconOrder = [
        'projectsIcon', 
        'associatesIcon', 
        'pagesIcon', 
        'aboutIcon', 
        'servicesIcon', 
        'journalIcon', 
        'teamIcon', 
        'settingsIcon', 
        'developerIcon', 
        'usersIcon', 
        'databaseIcon', 
        'devSettingsIcon', 
        'logoutIcon'
    ];

    const getIconLabel = (key: string) => {
        return key.replace(/([A-Z])/g, ' $1').replace('Icon', '').trim();
    };

    return (
        <div>
            {editingIcon && (
                <SvgEditModal 
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    onSave={handleSaveIcon}
                    iconName={editingIcon.label}
                    initialIcon={editingIcon.icon}
                />
            )}

            <div className="flex justify-between items-center mb-6">
                <div className="flex items-center gap-3">
                    <CodeIcon className="h-8 w-8 text-gray-800" />
                    <h2 className="text-3xl font-bold text-gray-800">Developer Settings</h2>
                </div>
            </div>

             <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-8 rounded-r-lg">
                <div className="flex">
                    <div className="flex-shrink-0">
                        <AlertTriangleIcon className="h-5 w-5 text-yellow-400" />
                    </div>
                    <div className="ml-3">
                        <p className="text-sm text-yellow-700">
                            <b>Warning:</b> These are advanced settings. Incorrect values, especially invalid SVG or JSON, may break parts of the admin panel or the public site. Proceed with caution.
                        </p>
                    </div>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-8">
                {/* CMS Icons Section */}
                <div className="bg-white p-8 rounded-lg shadow-md">
                    <h3 className="text-xl font-semibold text-gray-800 mb-2 border-b pb-2">CMS Sidebar Icons</h3>
                    <p className="text-sm text-gray-500 mb-4">
                        Click an icon to edit its SVG code. Use the color picker to set a uniform color for all icons.
                    </p>
                    
                    <div className="mb-6">
                        <label htmlFor="adminIconColor" className="block text-sm font-medium text-gray-700">Icon Color</label>
                        <div className="relative mt-1 w-full max-w-xs">
                            <input 
                                type="text" 
                                name="adminIconColor" 
                                id="adminIconColor" 
                                value={formState.theme.backendTheme.adminIconColor} 
                                onChange={handleThemeChange} 
                                className="block w-full pl-12 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-gray-500 focus:border-gray-500 text-sm"
                            />
                            <div className="absolute inset-y-0 left-0 flex items-center">
                                <input 
                                    type="color" 
                                    value={formState.theme.backendTheme.adminIconColor} 
                                    onChange={handleThemeChange} 
                                    name="adminIconColor" 
                                    className="h-full w-10 rounded-l-md border-0 cursor-pointer"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-wrap gap-4">
                        {iconOrder.map(iconKey => (
                            formState.developer[iconKey] && <div key={iconKey} className="text-center">
                                <button
                                    type="button"
                                    onClick={() => handleOpenIconModal(iconKey)}
                                    style={{ backgroundColor: settings.theme.backendTheme.adminSidebarColor || '#1f2937' }}
                                    className="w-24 h-24 rounded-md flex items-center justify-center group transition-transform hover:scale-105"
                                >
                                    <DynamicIcon 
                                        icon={formState.developer[iconKey]}
                                        className="w-10 h-10 transition-transform group-hover:scale-110"
                                        color={formState.theme.backendTheme.adminIconColor}
                                    />
                                </button>
                                <label className="block text-xs font-medium text-gray-600 mt-2 capitalize">
                                    {getIconLabel(iconKey)}
                                </label>
                            </div>
                        ))}
                    </div>
                </div>

                 {/* Feature Flags Section */}
                <div className="bg-white p-8 rounded-lg shadow-md">
                    <h3 className="text-xl font-semibold text-gray-800 mb-4 border-b pb-2">Feature Flags</h3>
                    <div className="space-y-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <label htmlFor="enableAiFeatures" className="block text-sm font-medium text-gray-700">Enable AI Features</label>
                                <p className="mt-1 text-xs text-gray-500">Globally enable or disable AI content generation features.</p>
                            </div>
                            <ToggleSwitch 
                                enabled={formState.enableAiFeatures} 
                                onChange={() => setFormState(prev => ({...prev, enableAiFeatures: !prev.enableAiFeatures}))} 
                            />
                        </div>
                         <div className="flex items-center justify-between border-t pt-4">
                            <div>
                                <label htmlFor="enableJournal" className="block text-sm font-medium text-gray-700">Enable Journal Feature</label>
                                <p className="mt-1 text-xs text-gray-500">Globally enable or disable the Journal section on both admin and public sites.</p>
                            </div>
                             <ToggleSwitch 
                                enabled={formState.enableJournal} 
                                onChange={() => setFormState(prev => ({...prev, enableJournal: !prev.enableJournal}))} 
                            />
                        </div>
                    </div>
                </div>

                {/* Upload Settings */}
                 <div className="bg-white p-8 rounded-lg shadow-md">
                    <h3 className="text-xl font-semibold text-gray-800 mb-4 border-b pb-2">Upload Settings</h3>
                    <div>
                        <label htmlFor="maxProjectImageSizeMB" className="block text-sm font-medium text-gray-700">Max Project Image Size (MB)</label>
                        <input 
                            type="number" 
                            name="maxProjectImageSizeMB" 
                            id="maxProjectImageSizeMB"
                            value={formState.maxProjectImageSizeMB || 2}
                            onChange={(e) => setFormState(prev => ({...prev, maxProjectImageSizeMB: Number(e.target.value)}))}
                            className="mt-1 block w-full max-w-xs px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-gray-500 focus:border-gray-500"
                            min="1"
                        />
                        <p className="mt-1 text-xs text-gray-500">Set the maximum file size for images uploaded to projects.</p>
                    </div>
                </div>

                {/* Site Configuration Section */}
                 <div className="bg-white p-8 rounded-lg shadow-md">
                    <h3 className="text-xl font-semibold text-gray-800 mb-4 border-b pb-2">Site Configuration (JSON)</h3>
                     <p className="text-sm text-gray-500 mb-4">Advanced configuration for the site. Must be valid JSON.</p>
                     <div>
                        <textarea
                            value={formState.siteConfig}
                            onChange={(e) => handleSiteConfigChange(e.target.value)}
                            rows={10}
                            className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-gray-500 font-mono text-sm ${jsonError ? 'border-red-500' : 'border-gray-300'}`}
                            placeholder="{}"
                        />
                        {jsonError && <p className="mt-2 text-sm text-red-600">{jsonError}</p>}
                     </div>
                </div>

                <div className="flex justify-end items-center space-x-4 sticky bottom-0 bg-white/80 backdrop-blur-sm py-4 rounded-b-lg">
                    <button type="submit" disabled={isSaving} className="px-8 py-3 bg-gray-800 text-white rounded-md hover:bg-gray-700 text-base font-semibold disabled:bg-gray-400">
                        {isSaving ? 'Saving...' : 'Save Developer Settings'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default AdminDeveloperPage;